import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  INTEGRATION_STATUS,
  IntegrationsFilters,
  IntegrationsResponse,
} from 'voxloud-types/dist/models/integrations/index';

import { AccountService, SessionService } from '../components';
import { PAGE_SIZE } from '../model/pagination.model';

@Injectable({
  providedIn: 'root',
})
export class IntegrationService {
  public hasAiAgent$ = new BehaviorSubject<boolean>(false);
  public get hasAiAgent(): boolean {
    return this.hasAiAgent$.value;
  }

  private cancelCheck$ = new Subject<void>();
  private headers = new HttpHeaders({
    Authorization: 'Bearer ' + this.account.current.token,
    'Voverc-Jwt-Auth': this.account.current.jwtToken,
  });

  constructor(
    private http: HttpClient,
    private account: AccountService,
    private currentSession: SessionService
  ) {}

  public setHasAiAgent(): void {
    this.cancelCheck$.next();
    this.hasAiAgent$.next(false);

    const companyId = this.currentSession.current.companyId;
    forkJoin({
      getRomulusIntegration: this.getIntegrationsByFilter(0, PAGE_SIZE, {
        company_id: companyId,
        platform_name: 'ROMULUS',
        enabled: true,
        status: INTEGRATION_STATUS.ACTIVE,
      }).pipe(catchError(() => of(null))),
      getVapiIntegration: this.getIntegrationsByFilter(0, PAGE_SIZE, {
        company_id: companyId,
        platform_name: 'VAPI',
        enabled: true,
        status: INTEGRATION_STATUS.ACTIVE,
      }).pipe(catchError(() => of(null))),
    })
      .pipe(takeUntil(this.cancelCheck$))
      .subscribe({
        next: (res) => {
          const hasRomulus = (res.getRomulusIntegration?.content.length ?? 0) > 0;
          const hasVapi = (res.getVapiIntegration?.content.length ?? 0) > 0;
          this.hasAiAgent$.next(hasRomulus || hasVapi);
        },
      });
  }

  public getIntegrationsByFilter(
    idPage: number,
    size: number,
    filters: IntegrationsFilters
  ): Observable<IntegrationsResponse> {
    let params = new HttpParams().append('page', idPage).append('size', size);

    if (filters && filters?.company_id) {
      params = params.append('company_id', filters.company_id);
    }
    if (filters && filters?.status) {
      params = params.append('status', filters.status);
    }
    if (filters && filters?.platform_id) {
      params = params.append('platform_id', filters.platform_id);
    }
    if (filters && filters?.platform_name) {
      params = params.append('platform_name', filters.platform_name);
    }
    if (filters && filters?.enabled) {
      params = params.append('enabled', filters.enabled);
    }

    return this.http.get<IntegrationsResponse>(`${environment.API_URL}/api/v1/integrations`, {
      params,
      headers: this.headers,
    });
  }
}
